import { useEffect, useState } from 'react';
import api from './api';

/* One shared poller for `/api/events/upcoming` (the class running now, else the next one) — the dashboard,
   the bottom "My Class" tab and the live banner all read the same record instead of each polling on their own. */
const POLL_MS = 60_000;
let cache;
let cacheUser = null;
let loadedAt = 0;
let inflight = null;
let timer = null;
const listeners = new Set();

function load() {
  if (!inflight) {
    inflight = api.get('/api/events/upcoming')
      .then((r) => { cache = r.data?.event || null; loadedAt = Date.now(); listeners.forEach((fn) => fn(cache)); })
      .catch(() => {})
      .finally(() => { inflight = null; });
  }
  return inflight;
}

export function refreshNextClass() { return load(); }

export function resetNextClassCache() { cache = undefined; cacheUser = null; loadedAt = 0; }

/** The next / running class for this user (null when none, undefined while loading). */
export function useNextClass(uid, enabled = true) {
  const [ev, setEv] = useState(() => (cacheUser === uid ? cache : undefined));
  useEffect(() => {
    if (!enabled || !uid) return undefined;
    if (cacheUser !== uid) { cache = undefined; cacheUser = uid; loadedAt = 0; }
    listeners.add(setEv);
    if (cache !== undefined) setEv(cache);
    if (cache === undefined || Date.now() - loadedAt > POLL_MS) load();
    if (!timer) timer = setInterval(load, POLL_MS);
    return () => {
      listeners.delete(setEv);
      if (!listeners.size && timer) { clearInterval(timer); timer = null; }
    };
  }, [uid, enabled]);
  return ev;
}

const pad = (n) => String(n).padStart(2, '0');

/** "3d 2h" · "11h 46m" · "12m" · "LIVE" · "" (none / over) for the bottom-tab mini timer. */
export function classTimerLabel(ev, now = Date.now()) {
  if (!ev) return { label: '', state: 'none' };
  const start = new Date(ev.start_at).getTime();
  const end = ev.end_at ? new Date(ev.end_at).getTime() : start + (ev.duration_minutes || 60) * 60_000;
  if (now >= start && now < end) return { label: 'LIVE', state: 'live' };
  if (now >= end) return { label: '', state: 'past' };
  const left = start - now;
  const d = Math.floor(left / 86_400_000), h = Math.floor((left % 86_400_000) / 3_600_000), m = Math.floor((left % 3_600_000) / 60_000);
  const label = d > 0 ? `${d}d ${h}h` : h > 0 ? `${h}h ${pad(m)}m` : `${Math.max(1, m)}m`;
  return { label, state: left <= 30 * 60_000 ? 'soon' : 'upcoming' };
}

/** Where a tap on the running class should go: the in-app room, the pasted link, or the classes page. */
export function liveClassTarget(ev) {
  if (!ev || ev.is_locked) return { to: '/group-classes' };
  if (ev.in_app) return { to: `/live/class/${ev.id}` };
  const link = String(ev.zoom_link || '').trim();
  if (link) return { href: /^[a-z][a-z0-9+.-]*:/i.test(link) ? link : `https://${link}` };
  return { to: '/group-classes' };
}
